import { useUnit } from 'effector-react'
import { useMutation, useQueryClient } from 'react-query'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { $creatorId } from '@/models/creators'
import { toast } from '@/models/toasts'
import { audioService } from '@/modules/chatting/service/audio'
import type { Audio } from '@/modules/chatting/types'
import { handleError } from '@/utils/error'

import { DialogProps } from '../types'

interface Props extends DialogProps {
  audio: Audio
}

export const DeleteAudioDialog = ({ isOpen, onClose, audio }: Props) => {
  const creatorId = useUnit($creatorId) ?? 0
  const queryClient = useQueryClient()

  const deleteAudioMutation = useMutation(
    () => audioService.deleteAudio({ creatorId, id: audio.id }),
    {
      onError: (error) => {
        handleError(error, 'Failed to remove audio file')
      },
      onSuccess: () => {
        toast.success('Successfully removed audio.')
        onClose()
        queryClient.setQueryData<Audio[]>(['audios', creatorId], (oldData) => {
          if (!oldData) return []

          return oldData.filter(({ id }) => audio.id !== id)
        })
      },
    },
  )

  return (
    <Dialog
      panelClassName="lg:max-w-[533px]"
      show={isOpen}
      onClose={onClose}
      bigDialog
    >
      <Dialog.Header>
        <Dialog.Title>Delete Audio</Dialog.Title>
      </Dialog.Header>
      <Dialog.Description className="mt-[8px] block text-center">
        Are you sure you want to delete the{' '}
        <span className="font-semibold text-title">{audio.name}</span> audio
        file?
      </Dialog.Description>
      <Dialog.ClosableFooter onClose={onClose}>
        <Button
          onClick={() => deleteAudioMutation.mutate()}
          isLoading={deleteAudioMutation.isLoading}
          variant="destructive"
        >
          Delete
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
